/**
 * MEDIA FLOOR — the ONE-VOICE LAW (docs/PRESENTER_AGENT.md): a narrated
 * video is a second speaker. The agent yields the floor or takes it,
 * never shares it — two narrators at once is incomprehensible.
 *
 * In a live call the floor follows the bot's AUDIBLE talking (the
 * per-utterance `botTalking` signal from its bot-speaking app messages;
 * the state machine's 'speaking' is track-level and holds all call) or
 * a viewer barge ('interrupted') — either PAUSES the narrated media on
 * stage; the bot going quiet auto-resumes (after a short debounce)
 * anything WE paused — never a video the viewer paused themselves. In
 * text mode there is
 * no audio collision — media plays through; the floor only reports
 * `isHolding()` so the presenter's continuation tick stands down while
 * a clip narrates, and `noteEnded` is the clip's own tick: the agent's
 * cue to retake the thread the moment the video finishes.
 *
 * Controllable media (native <video>, YouTube, Vimeo via their
 * postMessage protocols) registers a MediaControl. Long-tail providers
 * (wistia/loom/vidyard/…) we can't pause or read — `holdWhileMounted`
 * inverts the rule: the AGENT holds the silence while one is on stage.
 */

import { effect, type Signal } from '@preact/signals';
import { botTalking, voiceState, type VoiceState } from './voice/voice-state';

export type MediaControl = {
  pause: () => void;
  resume: () => void;
};

export type MediaFloorDeps = {
  /** A narrated video finished on its own — the agent retakes the floor. */
  onEnded?: (contentId: string, title: string | null) => void;
  /** Test seams; default to the real timers. */
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
};

/** Floor stays quiet this long after the bot stops before media resumes —
    a barge answer usually follows within a beat; resuming in the gap
    would stutter the clip. (The off-edge itself is the played-out
    silence, not the generation end — voice/playout-monitor.ts.) */
export const RESUME_DEBOUNCE_MS = 1000;

export type MediaFloor = ReturnType<typeof createMediaFloor>;

export function createMediaFloor(
  deps: MediaFloorDeps = {},
  state: Signal<VoiceState> = voiceState,
  talking: Signal<boolean> = botTalking,
) {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));

  let control: MediaControl | null = null;
  let playing = false;
  /** Uncontrollable narrated embeds currently mounted. */
  let held = 0;
  /** WE paused it (floor taken) — only then does the floor resume it. */
  let autoPaused = false;
  let resumeTimer: ReturnType<typeof setTimeout> | null = null;

  // The floor is taken while the bot is AUDIBLY talking (per-utterance
  // botTalking — NOT the track-level 'speaking' state, which holds for
  // the whole call) or the viewer barged (transient 'interrupted').
  const taken = () => talking.value || state.value.name === 'interrupted';

  /** Re-evaluated on every signal change AND whenever media starts —
      a clip beginning while the bot is mid-sentence pauses right away. */
  const apply = () => {
    if (taken()) {
      if (resumeTimer) {
        clearTimer(resumeTimer);
        resumeTimer = null;
      }
      if (playing && control) {
        autoPaused = true;
        control.pause();
      }
    } else if (autoPaused && control && !resumeTimer) {
      resumeTimer = setTimer(() => {
        resumeTimer = null;
        if (!taken() && autoPaused && control) {
          autoPaused = false;
          control.resume();
        }
      }, RESUME_DEBOUNCE_MS);
    }
  };

  const stop = effect(apply);

  return {
    /** Controllable narrated media mounted; returns the unregister. */
    register(c: MediaControl): () => void {
      control = c;
      return () => {
        if (control === c) {
          control = null;
          playing = false;
          autoPaused = false;
        }
      };
    },
    /** Uncontrollable narrated media mounted — the agent holds the silence. */
    holdWhileMounted(): () => void {
      held += 1;
      let done = false;
      return () => {
        if (!done) {
          done = true;
          held -= 1;
        }
      };
    },
    /** From the media's own play/pause events (covers manual control). */
    notePlaying(p: boolean): void {
      playing = p;
      if (p) {
        autoPaused = false;
        apply();
      }
    },
    noteEnded(contentId: string, title: string | null = null): void {
      playing = false;
      autoPaused = false;
      deps.onEnded?.(contentId, title);
    },
    /** Narrated media has the floor — the continuation tick stands down. */
    isHolding(): boolean {
      return playing || autoPaused || held > 0;
    },
    dispose(): void {
      if (resumeTimer) clearTimer(resumeTimer);
      stop();
    },
  };
}

// ---------- singleton (index.tsx inits; content-panel reads) ----------

let floor: MediaFloor | null = null;

export function initMediaFloor(deps: MediaFloorDeps): MediaFloor {
  floor = createMediaFloor(deps);
  return floor;
}

export function mediaFloor(): MediaFloor | null {
  return floor;
}

// ---------- remote-player postMessage protocols (YouTube / Vimeo) ----------

export type EmbedProvider = 'youtube' | 'vimeo' | null;

export function embedProviderOf(url: string): EmbedProvider {
  if (/(^|\.)youtube(-nocookie)?\.com|youtu\.be/i.test(url)) return 'youtube';
  if (/(^|\.)vimeo\.com/i.test(url)) return 'vimeo';
  return null;
}

export type EmbedMediaEvent = 'playing' | 'paused' | 'ended' | null;

/**
 * One message from a YT (`enablejsapi=1`) or Vimeo iframe → our event.
 * YT posts JSON strings ({event:'onStateChange'|'infoDelivery',
 * info.playerState: 1 playing · 2 paused · 0 ended); Vimeo posts
 * objects/strings ({event:'play'|'pause'|'finish'} after
 * addEventListener). Anything else → null.
 */
export function parseEmbedMessage(data: unknown): EmbedMediaEvent {
  let msg: unknown = data;
  if (typeof msg === 'string') {
    try {
      msg = JSON.parse(msg);
    } catch {
      return null;
    }
  }
  if (typeof msg !== 'object' || msg === null) return null;
  const m = msg as { event?: unknown; info?: unknown };
  if (m.event === 'play') return 'playing';
  if (m.event === 'pause') return 'paused';
  if (m.event === 'finish') return 'ended';
  const info = m.info as { playerState?: unknown } | undefined;
  const s = typeof info?.playerState === 'number' ? info.playerState : null;
  if (s === 1) return 'playing';
  if (s === 2) return 'paused';
  if (s === 0) return 'ended';
  return null;
}

/** The command each provider understands, ready to postMessage. */
export function embedCommand(provider: 'youtube' | 'vimeo', op: 'pause' | 'play'): string {
  return provider === 'youtube'
    ? JSON.stringify({ event: 'command', func: op === 'pause' ? 'pauseVideo' : 'playVideo', args: [] })
    : JSON.stringify({ method: op });
}

/** Sent once on load so the iframe starts posting events to us. */
export function embedHandshakes(provider: 'youtube' | 'vimeo'): string[] {
  return provider === 'youtube'
    ? [JSON.stringify({ event: 'listening', id: 'sp-media-floor' })]
    : ['play', 'pause', 'finish'].map((v) => JSON.stringify({ method: 'addEventListener', value: v }));
}
